import { useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { db } from "../../firebase";
import { defaultBooking, BOOKING_SOURCE, EVENT_TYPE_LABELS } from "./schema";
import { useBookings } from "./hooks/useBookings";
import { findMatchingClients, createClient } from "./clientResolver";
import { useDictation, DictateButton } from "../../hooks/useDictation";

import { STAFF_THEME, FONT } from "../../theme/staff";
const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

const FIELD_LABELS = {
  name: "Name",
  company: "Company",
  email: "Email",
  phone: "Phone",
  dayOfContactName: "Day-of contact name",
  dayOfContactPhone: "Day-of contact phone",
  eventName: "Event name",
  eventType: "Event type",
  seriesName: "Series",
  confirmedDate: "Confirmed date",
  confirmedStart: "Confirmed start",
  confirmedEnd: "Confirmed end",
  inquiryEventDate: "Inquiry event date",
  preferredTime: "Preferred time",
  flexibleDate: "Flexible on date",
  venueName: "Venue name",
  venueAddress: "Venue address",
  location: "Location",
  indoorOutdoor: "Indoor / outdoor",
  attendance: "Expected attendance",
  services: "Services",
  guestExperience: "Guest experience",
  performanceFormat: "Performance format",
  departureFrom: "Departure from",
  budget: "Budget",
  totalRate: "Total rate",
  clientNotes: "Client notes",
};

const DATE_FIELDS = new Set(["inquiryEventDate", "confirmedDate"]);
const DATETIME_FIELDS = new Set(["confirmedStart", "confirmedEnd"]);

// Fields the clone carries forward from a past event in the same series.
// Scope = logistics + setup only. Money (rate/quote/budget) and contact info
// are intentionally NOT carried — Dan sets fresh pricing each time and the
// organizer/contact may differ. (See clone-scope decision, 2026-05-28.)
const CLONE_CARRY_FIELDS = [
  "eventName", "eventType",
  "venueName", "venueAddress", "location", "indoorOutdoor",
  "services", "guestExperience", "performanceFormat", "departureFrom",
];

const pillStyle = {
  background: BRAND.panel,
  color: BRAND.cream,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "999px",
  padding: "0.4rem 0.85rem",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.85rem",
};

const pad2 = (n) => String(n).padStart(2, "0");

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "number") return new Date(ts);
  return null;
}

// Same normalization as functions/index.js::backfillSeriesIds so a cloned
// booking lands in the same series bucket as its template.
function makeSeriesId(name) {
  return (name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

// Take the template's clock time, staple it onto the new date. Returns a local
// "YYYY-MM-DDTHH:MM" string that flows through the existing datetime handling.
function cloneTimeOfDay(newDateStr, templateTs) {
  const t = toDate(templateTs);
  if (!t || !newDateStr) return null;
  const d = new Date(newDateStr + "T00:00:00");
  d.setHours(t.getHours(), t.getMinutes(), 0, 0);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function fmtValue(key, value) {
  if (value == null || value === "") return null;
  if (key === "flexibleDate") return value ? "Yes" : null;
  if (key === "eventType") return EVENT_TYPE_LABELS[value] || String(value);
  if (DATE_FIELDS.has(key)) {
    const d = new Date(value + "T00:00:00");
    if (isNaN(d)) return String(value);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  }
  if (DATETIME_FIELDS.has(key)) {
    const d = new Date(value);
    if (isNaN(d)) return String(value);
    return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  }
  if (key === "totalRate") return `$${value}`;
  return String(value);
}

// Radio-style selectable row for the client-link choice in the preview.
function ClientOption({ selected, onClick, title, subtitle }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: "0.6rem", width: "100%", textAlign: "left",
      background: selected ? BRAND.bg : "transparent",
      border: `1px solid ${selected ? BRAND.gold : BRAND.line}`,
      borderRadius: "8px", padding: "0.55rem 0.7rem", marginBottom: "0.4rem", cursor: "pointer",
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
        border: `2px solid ${selected ? BRAND.gold : BRAND.muted}`,
        background: selected ? BRAND.gold : "transparent",
      }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", color: selected ? BRAND.gold : BRAND.cream, fontWeight: 700, fontSize: "0.85rem" }}>{title}</span>
        {subtitle && <span style={{ display: "block", color: BRAND.muted, fontSize: "0.76rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</span>}
      </span>
    </button>
  );
}

export default function QuickAddFlow({ navigate }) {
  const [stage, setStage] = useState("input"); // "input" | "preview"
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [cloneInfo, setCloneInfo] = useState(null); // { seriesName, templateDateLabel } | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Client matching: matches found by email/phone, and the operator's choice.
  // clientChoice: "" (unset) | "new" | "none" | <clientId>. We never link
  // without an explicit choice (confirm-before-link).
  const [clientMatches, setClientMatches] = useState([]);
  const [clientChoice, setClientChoice] = useState("");
  const dictation = useDictation(text, setText);
  const { bookings } = useBookings();

  // displayName -> bookings sharing it. Keyed on seriesName||eventName, the same
  // grouping the CRM list uses. Excludes test bookings so we never clone fakes.
  const seriesIndex = useMemo(() => {
    const map = new Map();
    for (const b of bookings) {
      if (b._isTestData) continue;
      const name = (b.seriesName || b.eventName || "").trim();
      if (!name) continue;
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(b);
    }
    return map;
  }, [bookings]);

  // Pick the best template in a group: prefer one with real venue/timing data,
  // then the most recent occurrence so cloned timing reflects the latest setup.
  function pickTemplate(name) {
    const list = seriesIndex.get(name);
    if (!list || !list.length) return null;
    const withData = list.filter((b) => b.venueName || b.venueAddress || b.confirmedStart);
    const pool = withData.length ? withData : list;
    const ms = (b) => {
      const d = toDate(b.confirmedDate) || toDate(b.submittedAt);
      return d ? d.getTime() : 0;
    };
    return pool.slice().sort((a, b) => ms(b) - ms(a))[0];
  }

  // AI returns matchedSeriesName; fall back to a normalized eventName match so a
  // near-exact name still clones even if the model declined to match.
  function resolveTemplateName(matchedSeriesName, parsed) {
    if (matchedSeriesName && seriesIndex.has(matchedSeriesName)) return matchedSeriesName;
    const pn = makeSeriesId(parsed.eventName || "");
    if (!pn) return null;
    for (const name of seriesIndex.keys()) {
      if (makeSeriesId(name) === pn) return name;
    }
    return null;
  }

  function buildDraft(parsed, template) {
    const out = { ...parsed };
    if (!template) return out;

    for (const k of CLONE_CARRY_FIELDS) {
      if (template[k] != null && template[k] !== "") out[k] = template[k];
    }

    // Series identity so the new booking groups with its siblings.
    const seriesName = template.seriesName || template.eventName || null;
    out.seriesName = seriesName;
    out.seriesId = template.seriesId || makeSeriesId(seriesName);

    // Keep the template's time-of-day, swap onto the resolved new date.
    const newDate = parsed.confirmedDate ? String(parsed.confirmedDate).slice(0, 10) : null;
    if (newDate) {
      if (!parsed.confirmedStart && template.confirmedStart) {
        out.confirmedStart = cloneTimeOfDay(newDate, template.confirmedStart);
      }
      if (!parsed.confirmedEnd && template.confirmedEnd) {
        out.confirmedEnd = cloneTimeOfDay(newDate, template.confirmedEnd);
      }
    }
    return out;
  }

  const onParse = async () => {
    if (!text.trim()) return;
    setParsing(true);
    setError(null);
    try {
      const now = new Date();
      const today = `${now.toLocaleDateString("en-US", { weekday: "long" })} ${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
      const knownSeries = Array.from(seriesIndex.keys());

      const fn = httpsCallable(getFunctions(getApp()), "bookingQuickAddParse");
      const res = await fn({ text, today, knownSeries });
      const parsed = res.data.fields || {};

      const templateName = resolveTemplateName(res.data.matchedSeriesName, parsed);
      const template = templateName ? pickTemplate(templateName) : null;

      setDraft(buildDraft(parsed, template));
      if (template) {
        const tDate = toDate(template.confirmedDate);
        setCloneInfo({
          seriesName: template.seriesName || template.eventName || templateName,
          templateDateLabel: tDate
            ? tDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : null,
        });
      } else {
        setCloneInfo(null);
      }
      // Look for an existing client. If none, default to creating one (when we
      // have any contact info); if matches exist, force an explicit pick.
      let matches = [];
      try {
        matches = await findMatchingClients({ email: parsed.email, phone: parsed.phone });
      } catch (e) {
        matches = []; // matching is best-effort — never block the booking
      }
      setClientMatches(matches);
      const hasContact = Boolean(parsed.email || parsed.phone || parsed.name);
      setClientChoice(matches.length === 0 ? (hasContact ? "new" : "none") : "");
      setStage("preview");
    } catch (err) {
      setError(err.message || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  const onConfirm = async () => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      const payload = { ...defaultBooking() };
      for (const k of Object.keys(draft)) {
        const v = draft[k];
        if (v == null || v === "") {
          payload[k] = null;
        } else if (DATE_FIELDS.has(k)) {
          const d = new Date(String(v).slice(0, 10) + "T00:00:00");
          payload[k] = isNaN(d) ? null : Timestamp.fromDate(d);
        } else if (DATETIME_FIELDS.has(k)) {
          const d = new Date(v);
          payload[k] = isNaN(d) ? null : Timestamp.fromDate(d);
        } else {
          payload[k] = v;
        }
      }
      payload.source = BOOKING_SOURCE.QUICK_ADD;
      // If a confirmed date came through, treat this as a real scheduled
      // booking rather than a raw inquiry — saves a manual status flip.
      payload.status = payload.confirmedDate ? "booked" : "new_inquiry";
      payload.statusChangedAt = serverTimestamp();
      payload.submittedAt = serverTimestamp();
      payload.createdAt = serverTimestamp();
      payload.updatedAt = serverTimestamp();
      payload.clientNotes = [payload.clientNotes, text && `\n\n--- Original text ---\n${text}`].filter(Boolean).join("");
      // Resolve the client link from the operator's choice.
      if (clientChoice === "new") {
        payload.clientId = await createClient({
          name: draft.name, company: draft.company, email: draft.email, phone: draft.phone,
        });
      } else if (clientChoice && clientChoice !== "none") {
        payload.clientId = clientChoice; // existing client id
      } else {
        payload.clientId = null;
      }
      const ref = await addDoc(collection(db, "bookings"), payload);
      navigate(`/crm/bookings/${ref.id}`);
    } catch (err) {
      setError("Save failed: " + err.message);
      setSaving(false);
    }
  };

  const rows = draft
    ? Object.entries(FIELD_LABELS)
        .map(([key, label]) => [key, label, fmtValue(key, draft[key])])
        .filter(([, , v]) => v != null)
    : [];

  const cloneMissingDate = Boolean(cloneInfo && !(draft && draft.confirmedDate));

  return (
    <div style={{
      background: BRAND.bg,
      minHeight: "100vh",
      color: BRAND.text,
      fontFamily: FONT.body,
      padding: "1rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <h1 style={{ margin: 0, fontSize: "1.3rem", color: BRAND.gold, flex: 1 }}>⚡ Quick Add</h1>
          <button type="button" onClick={() => navigate("/crm")} style={pillStyle}>← Bookings</button>
        </div>

        {error && (
          <div style={{
            background: "#6b2234", color: "white",
            padding: "0.7rem 1rem", borderRadius: "8px", marginBottom: "1rem", fontSize: "0.85rem",
          }}>
            {error}
          </div>
        )}

        {stage === "input" && (
          <>
            <p style={{ color: BRAND.muted, fontSize: "0.9rem", marginTop: 0 }}>
              Paste an email or talk it out loud — read the text you got, list what you know about the event. Claude will extract what it can into a booking draft; you'll review before saving.
            </p>
            <p style={{ color: BRAND.muted, fontSize: "0.82rem", marginTop: "-0.4rem" }}>
              For a recurring event, just say it plainly — e.g. <em>"we're booked at the Tower Grove Farmers Market this Saturday."</em> It'll recognize the series, pull venue &amp; timing from your last one, and slot in the new date.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.45rem" }}>
              <DictateButton
                recording={dictation.recording}
                interim={dictation.interim}
                speechSupported={dictation.speechSupported}
                onStart={dictation.start}
                onStop={dictation.stop}
                error={dictation.error}
                disabled={parsing}
              />
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={dictation.recording ? "Listening — speak freely…" : "Paste or dictate here…"}
              rows={14}
              style={{
                width: "100%",
                background: BRAND.panel,
                border: `1px solid ${dictation.recording ? "#ef537e" : BRAND.line}`,
                borderRadius: "10px",
                color: BRAND.text,
                padding: "0.9rem",
                fontSize: "0.95rem",
                fontFamily: "inherit",
                outline: "none",
                resize: "vertical",
                boxSizing: "border-box",
                transition: "border-color 0.2s",
              }}
            />
            <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onParse}
                disabled={parsing || !text.trim()}
                style={{
                  background: parsing || !text.trim() ? BRAND.panel : BRAND.gold,
                  color: parsing || !text.trim() ? BRAND.muted : BRAND.bg,
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.7rem 1.3rem",
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  cursor: parsing || !text.trim() ? "default" : "pointer",
                }}
              >
                {parsing ? "Parsing with Claude…" : "✨ Extract booking fields"}
              </button>
            </div>
          </>
        )}

        {stage === "preview" && draft && (
          <>
            {cloneInfo && (
              <div style={{
                background: "#2c2410",
                border: `1px solid ${BRAND.gold}`,
                borderRadius: "10px",
                padding: "0.8rem 1rem",
                marginBottom: "1rem",
                fontSize: "0.88rem",
                lineHeight: 1.45,
              }}>
                <div style={{ color: BRAND.gold, fontWeight: 800, marginBottom: "0.2rem" }}>
                  🔁 Matched the “{cloneInfo.seriesName}” series
                </div>
                <div style={{ color: BRAND.cream }}>
                  Pre-filled venue, type &amp; timing from your{cloneInfo.templateDateLabel ? ` ${cloneInfo.templateDateLabel}` : " last"} event. Pricing &amp; contact were left blank on purpose — set those fresh. Review everything before saving.
                </div>
                {cloneMissingDate && (
                  <div style={{ color: "#ffb84a", fontWeight: 700, marginTop: "0.4rem" }}>
                    ⚠️ Couldn't pin a date from the text — set the confirmed date on the next screen.
                  </div>
                )}
              </div>
            )}

            <div style={{
              background: BRAND.panel,
              border: `1px solid ${BRAND.line}`,
              borderRadius: "10px",
              padding: "0.9rem",
              marginBottom: "1rem",
            }}>
              <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.5rem", letterSpacing: "0.04em" }}>
                {cloneInfo ? "DRAFT" : "EXTRACTED"} · {rows.length} field{rows.length === 1 ? "" : "s"}
              </div>
              {rows.length === 0 && (
                <div style={{ color: BRAND.muted, fontSize: "0.88rem" }}>
                  Nothing structured extracted. You can still create an empty booking and paste the text into Client Notes manually, or go back and try rephrasing.
                </div>
              )}
              {rows.map(([key, label, value]) => (
                <div key={key} style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: "0.8rem",
                  padding: "0.35rem 0",
                  borderBottom: `1px dashed ${BRAND.line}`,
                  fontSize: "0.88rem",
                }}>
                  <div style={{ color: BRAND.muted }}>{label}</div>
                  <div style={{ color: BRAND.cream, wordBreak: "break-word" }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Client link — confirm before linking (never auto-links) */}
            <div style={{
              background: BRAND.panel,
              border: `1px solid ${clientMatches.length ? BRAND.gold : BRAND.line}`,
              borderRadius: "10px",
              padding: "0.9rem",
              marginBottom: "1rem",
            }}>
              <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.5rem", letterSpacing: "0.04em" }}>
                👤 CLIENT
              </div>
              {clientMatches.length > 0 ? (
                <>
                  <div style={{ color: BRAND.cream, fontSize: "0.85rem", marginBottom: "0.6rem" }}>
                    {clientMatches.length === 1 ? "A matching client already exists" : `${clientMatches.length} matching clients exist`} — choose how to link this booking:
                  </div>
                  {clientMatches.map((c) => (
                    <ClientOption
                      key={c.id}
                      selected={clientChoice === c.id}
                      onClick={() => setClientChoice(c.id)}
                      title={`Link to ${c.name || c.company || c.email || "this client"}`}
                      subtitle={[c.company, c.email, c.phone].filter(Boolean).join(" · ")}
                    />
                  ))}
                  <ClientOption selected={clientChoice === "new"} onClick={() => setClientChoice("new")} title="➕ Create a new client instead" />
                  <ClientOption selected={clientChoice === "none"} onClick={() => setClientChoice("none")} title="Don't attach a client" />
                </>
              ) : (
                <>
                  <div style={{ color: BRAND.cream, fontSize: "0.85rem", marginBottom: "0.6rem" }}>
                    No existing client matches this contact.
                  </div>
                  <ClientOption
                    selected={clientChoice === "new"}
                    onClick={() => setClientChoice("new")}
                    title="➕ Create a new client record"
                    subtitle={[draft.name, draft.company, draft.email].filter(Boolean).join(" · ") || "from this booking's contact info"}
                  />
                  <ClientOption selected={clientChoice === "none"} onClick={() => setClientChoice("none")} title="Don't attach a client" />
                </>
              )}
            </div>

            {clientMatches.length > 0 && clientChoice === "" && (
              <div style={{ color: BRAND.gold, fontSize: "0.8rem", marginBottom: "0.6rem", textAlign: "right" }}>
                Choose a client option above to continue.
              </div>
            )}

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setStage("input")} style={pillStyle}>
                ← Back to raw text
              </button>
              {(() => {
                const blocked = saving || (clientMatches.length > 0 && clientChoice === "");
                return (
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={blocked}
                    style={{
                      background: blocked ? BRAND.panel : BRAND.green,
                      color: blocked ? BRAND.muted : "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.65rem 1.2rem",
                      fontWeight: 800,
                      fontSize: "0.92rem",
                      cursor: blocked ? "default" : "pointer",
                    }}
                  >
                    {saving ? "Creating…" : "Create & open in detail →"}
                  </button>
                );
              })()}
            </div>

            <p style={{ color: BRAND.muted, fontSize: "0.78rem", marginTop: "1rem" }}>
              The original raw text is appended to Client Notes so nothing gets lost. You can edit everything on the detail screen.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
